<template>
  <div
    class="email-page"
    :class="{ 'email-page--batch-banner': !!batchOutcomeBanner, 'email-page--pane-dragging': paneDragging }"
    :style="$_cssVars"
  >
    <v-card class="email-main-card mt-0 elevation-0 rounded-0 borderless">
      <v-card-title class="email-card-title-row flex-shrink-0 align-center">
        <span class="text-h6 font-weight-medium flex-shrink-0">Email</span>
        <information-dialog-icon
          class="ml-1 align-self-center email-info-trigger flex-shrink-0"
          confirm-class="mr-3 mb-1 title"
          confirm-color="secondary darken-1"
          :dialog-width="560"
          icon-color="grey darken-1"
          title="Email Information"
          tclass="headline grey lighten-2 mb-8"
        >
          <div class="mt-n2 email-info-dialog">
            <p>
              <strong>Mailbox</strong> — Pick one (the number is how many emails it has). Newest
              first. <strong>All mailboxes</strong> shows recent mail from everyone — not a full
              archive. <strong>Filter</strong> narrows this page; use the arrows for more pages.
            </p>
            <p v-if="needsEmailApi('connectedBucket')">
              <strong>Select</strong> — Trash emails or <strong>Connect to firm (as a note)</strong>. Connect
              saves the original as <strong>email.txt</strong> and/or <strong>email.html</strong> on a firm note. By default
              (strongly recommended) the email then goes to Trash — so it doesn’t get connected to
              another firm.
            </p>
            <p v-else>
              <strong>Select</strong> — Trash emails or <strong>Connect to firm (as a note)</strong>. Connect
              saves the original as <strong>email.txt</strong> and/or <strong>email.html</strong> on a firm note. By default the
              email then goes to Trash.
            </p>
            <p v-if="needsEmailApi('trashBucket')">
              <strong>Trash</strong> — Removes it from the message list. You can’t bring it back.
            </p>
            <p v-else>
              <strong>Trash</strong> — Puts it under the Trash tab so you can find it later.
            </p>
            <p>
              <strong>One vs several</strong> — One checked → opens the email. Several
              checked → Connect them all from this page.
            </p>
            <p v-if="$vuetify.breakpoint.mdAndUp">
              <strong>Open in</strong> — Toggle at top right: side pane next to the
              message list (default; full width when the window is narrow), full view, or a
              separate window. Saved in this browser. Drag the bar between list and pane to
              resize (double-click it to reset).
            </p>
            <!-- Phones: no split — the first two modes both present full width. -->
            <p v-else>
              <strong>Open in</strong> — Toggle at top right: open emails in this page
              (full width on this screen) or in a separate window. Saved in this browser.
            </p>
            <p class="mb-0">
              <strong>Open email</strong> — Set firm / Re / staff / status, then
              <strong>Connect</strong> or trash. Connect ▾ and <strong>Go to</strong>
              (default Older) save in this browser. Go to runs only after trash or a fully
              successful connect; on errors you stay so you can review or trash.
            </p>
          </div>
        </information-dialog-icon>
        <div
          v-if="selected.length"
          class="email-bulk-mobile d-flex align-center flex-shrink-0 grey lighten-4 rounded ml-2"
        >
          <span
            class="email-bulk-mobile-count font-weight-medium"
            :title="`${selected.length} selected`"
          >{{ selected.length }}<span class="email-bulk-mobile-count-label"> selected</span></span>
          <div class="email-bulk-mobile-actions d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn
                  icon
                  small
                  class="mr-0 email-connect-icon-btn email-bulk-action-btn"
                  :disabled="!canConnectSelection || busy"
                  :loading="connectSubmitting"
                  @click="onBulkConnectClick"
                >
                  <v-icon>mdi-link-variant</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Connect to firm (as a note)</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn
                  icon
                  small
                  class="mr-0 email-bulk-action-btn"
                  :disabled="!canTrashSelection || busy"
                  :loading="busy && !connectSubmitting"
                  @click="confirmTrash = true"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Move to trash</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small class="ml-0 email-bulk-clear-btn" :disabled="busy" v-on="on" @click="clearSelection">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Clear selection</span>
          </v-tooltip>
          </div>
        </div>
        <v-spacer class="email-title-spacer"/>
        <!-- 1 Jul 2026 JCH: hide tabs when only Primary is available. -->
        <div v-if="showBuckets" class="email-buckets email-buckets--title d-flex align-center flex-shrink-0">
          <span class="caption grey--text text--darken-1 mr-2 flex-shrink-0">Show</span>
          <v-btn-toggle
            v-model="bucketFilter"
            class="flex-shrink-0"
            dense
            mandatory
            @change="onBucketFilterChange"
          >
            <v-btn value="inbox" small text>
              Primary
            </v-btn>
            <v-btn v-if="!needsEmailApi('connectedBucket')" value="connected" small text>
              Connected
            </v-btn>
            <v-btn v-if="!needsEmailApi('trashBucket')" value="deleted" small text>
              Trash
            </v-btn>
          </v-btn-toggle>
        </div>
        <!-- 13 Jul 2026 JCH: how emails open — side pane (default), Gmail-style full view, or window.
             Saved in this browser; switching re-hosts the open email in place. -->
        <v-btn-toggle
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="openMode"
          class="ml-2 flex-shrink-0 email-open-mode-toggle"
          dense
          mandatory
          @change="onOpenModeChange"
        >
          <!-- 13 Jul 2026 JCH: always rendered — v-if'ing the selected button out makes the
               mandatory toggle auto-pick another mode and overwrite the pref. When the window
               is too narrow for the split, pane mode presents the open email full width. -->
          <v-btn
            value="pane"
            small
            text
            title="Open emails in a side pane next to the message list (full width when the window is narrow)"
            @click="onModeIconClick('pane')"
          >
            <v-icon small>mdi-dock-right</v-icon>
          </v-btn>
          <v-btn
            value="full"
            small
            text
            title="Open emails full width (back arrow returns to the message list)"
            @click="onModeIconClick('full')"
          >
            <v-icon small>mdi-fullscreen</v-icon>
          </v-btn>
          <v-btn value="window" small text title="Open emails in a separate window" @click="onModeIconClick('window')">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
        </v-btn-toggle>
        <!-- 13 Jul 2026 JCH: phones — pane and full view both present full width, so collapse
             to the two real choices. Separate toggle (not v-if'd buttons) keeps the saved
             pane/full pref intact for desktop. -->
        <v-btn-toggle
          v-else
          v-model="openModeMobile"
          class="ml-2 flex-shrink-0 email-open-mode-toggle"
          dense
          mandatory
        >
          <v-btn
            value="page"
            small
            text
            title="Open emails in this page (full width)"
            @click="onMobilePageIconClick"
          >
            <v-icon small>mdi-fullscreen</v-icon>
          </v-btn>
          <v-btn
            value="window"
            small
            text
            title="Open emails in a separate window"
            @click="onModeIconClick('window')"
          >
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
        </v-btn-toggle>
      </v-card-title>

      <div
        v-if="batchOutcomeBanner"
        class="email-batch-outcome flex-shrink-0 px-2 px-sm-3 pb-1"
      >
        <div class="email-batch-outcome-inner d-flex align-start white pa-2 pa-sm-3 rounded">
          <v-icon small class="mr-1 mt-1 flex-shrink-0" color="warning darken-2">
            mdi-alert-circle-outline
          </v-icon>
          <span class="body-2 flex-grow-1">{{ batchOutcomeBanner }}</span>
          <v-btn icon small class="ml-1 flex-shrink-0 mt-n1" title="Dismiss" @click="clearBatchOutcomeBanner">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card-text class="email-toolbar-text flex-shrink-0 pt-0 pb-1 px-2 px-sm-3">
        <div class="email-toolbar-single d-flex flex-wrap align-start">
          <!-- 1 Jul 2026 JCH: header hidden — select-all in toolbar.
               13 Jul 2026 JCH: disabled while full view hides the list — no blind bulk selections. -->
          <div
            class="email-selectall d-flex align-center flex-shrink-0"
            :title="fullViewerOpen ? 'Back to the message list to select emails' : 'Select all on this page'"
          >
            <v-simple-checkbox
              color="secondary"
              :disabled="loadingList || !visibleItems.length || fullViewerOpen"
              :indeterminate="someVisibleSelected"
              :ripple="false"
              :value="allVisibleSelected"
              @input="toggleSelectAll"
            />
          </div>
          <div class="email-mailbox-toolbar d-flex align-center flex-wrap mr-sm-4 mb-2 mb-sm-0">
            <v-select
              v-model="mailboxId"
              :items="mailboxItems"
              class="email-mailbox-select"
              dense
              hide-details
              item-text="label"
              item-value="id"
              :label="mailboxSelectLabel"
              :loading="mailboxesLoading"
              :disabled="mailboxesLoading"
              :menu-props="{ offsetY: true, offsetOverflow: false }"
              :no-data-text="mailboxesLoading ? 'Loading mailboxes…' : 'No mailboxes'"
              @change="onMailboxChange"
            />
          </div>
          <div class="email-search-wrap d-flex flex-column justify-center flex-grow-1 mr-2 mt-n2 mt-sm-0">
            <v-text-field
              v-model="listSearchInput"
              append-icon="mdi-magnify"
              class="email-search-field--toolbar flex-grow-1 mt-0 pt-0"
              clearable
              dense
              :hide-details="serverSearch"
              :hint="filterHint"
              persistent-hint
              :label="serverSearch ? 'Search' : 'Filter'"
              :placeholder="serverSearch ? 'Search…' : 'Filter loaded emails…'"
              single-line
              @click:clear="clearListSearch"
              @input="scheduleListSearch"
            />
          </div>
        </div>
      </v-card-text>

      <div ref="splitHost" class="email-split d-flex align-start px-2 px-sm-3 pb-1">
      <div v-show="!fullViewerOpen" ref="tableHost" class="email-table-host flex-grow-1">
        <v-data-table
          class="elevation-2 email-table email-table--gmail email-table--noheader"
          :class="{ 'email-table--with-to': showToColumn, 'email-table--footer-compact': footerCompact }"
          dense
          fixed-header
          :footer-props="footerProps"
          :headers="headers"
          :height="tableBodyHeight"
          :items="visibleItems"
          :items-per-page="pageSize"
          :loading="loadingList"
          :page="page"
          :server-items-length="tableServerLength"
          item-key="id"
          loading-text="Loading emails…"
          :mobile-breakpoint="0"
          show-select
          :item-class="itemRowClass"
          v-model="selected"
          @click:row="onRowClick"
          @update:items-per-page="onItemsPerPageChange"
          @update:page="onPageChange"
        >
          <template v-slot:item.subject="{ item }">
            <div class="email-subject-line">
              <span class="email-subject-text">{{ item.subject }}</span>
              <span v-if="item.snippet" class="email-snippet grey--text text--darken-1">
                &nbsp;— {{ item.snippet }}
              </span>
            </div>
          </template>
          <template v-slot:item.received_at="{ item }">
            <div
              class="email-date-wrap d-flex align-center justify-end"
              :class="{ 'email-date-wrap--actions': !item.deleted }"
            >
              <span class="email-date-text d-inline-flex align-center">
                <v-icon v-if="item.attachment_count" class="email-attach-icon mr-1" small>
                  mdi-paperclip
                </v-icon>
                {{ formatEmailListDate(item.received_at) }}
              </span>
              <span v-if="!item.deleted" class="email-row-actions">
                <v-btn
                  icon
                  small
                  class="email-connect-icon-btn"
                  title="Open to connect to firm (as a note)"
                  :disabled="busy || !!item.connected"
                  @click.stop="openMessageWindow(item)"
                >
                  <v-icon>mdi-link-variant</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  title="Move to trash"
                  :disabled="busy"
                  @click.stop="trashSingle(item)"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </span>
            </div>
          </template>
          <!-- 1 Jul 2026 JCH: token paging — range only, no page jump. -->
          <template v-slot:footer.page-text>
            {{ footerRangeText }}
          </template>
        </v-data-table>
      </div>
      <!-- 13 Jul 2026 JCH: grab bar — trade list width for pane width; saved in this browser. -->
      <div
        v-if="viewerOpen && !fullViewerOpen"
        class="email-split-divider flex-shrink-0"
        :style="{ height: tableBodyHeight }"
        title="Drag to resize the pane · double-click to reset"
        @mousedown="onPaneDividerDown"
        @dblclick="resetPaneWidth"
      />
      <div
        v-if="viewerOpen"
        ref="paneHost"
        class="d-flex flex-column"
        :class="fullViewerOpen ? 'email-full-view flex-grow-1' : 'email-reading-pane flex-shrink-0'"
        :style="paneStyle"
      >
        <div class="email-reading-pane-bar d-flex align-center flex-shrink-0 px-1 grey lighten-4">
          <v-btn
            v-if="fullViewerOpen"
            text
            small
            class="email-back-to-list-btn"
            @click="closePane"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Message list
          </v-btn>
          <v-spacer/>
          <!-- 13 Jul 2026 JCH: Gmail-style ‹ › browsing — no connect/trash, just look around. -->
          <span
            v-if="paneBrowsePos.index >= 0"
            class="email-browse-pos caption grey--text text--darken-1 mr-1"
            title="Position on this page of the message list"
          >{{ paneBrowsePos.index + 1 }} of {{ paneBrowsePos.count }}</span>
          <v-btn
            icon
            small
            class="email-browse-newer"
            title="Newer email"
            :disabled="!paneBrowsePos.hasNewer"
            @click="browsePane('newer')"
          >
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            class="email-browse-older mr-2"
            title="Older email"
            :disabled="!paneBrowsePos.hasOlder"
            @click="browsePane('older')"
          >
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn icon small title="Open in separate window" @click="movePaneToWindow">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon small title="Close" @click="closePane">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="email-reading-pane-body flex-grow-1">
          <EmailMessage
            embedded
            :pane-query="paneQuery"
            :list-api="paneListApi"
            :shared-client="client"
            @advance="onPaneAdvance"
            @close="closePane"
            @refresh="handleEmailRefresh"
          />
        </div>
      </div>
      </div>
    </v-card>

    <ActionDialog
      max-width="500"
      :value="confirmTrash"
      title="Move to trash?"
      confirm-text="Move to trash"
      confirm-color="secondary"
      divider
      close-text="Cancel"
      persistent
      :disabled="busy"
      :confirm-loading="busy && !connectSubmitting"
      @close="confirmTrash = false"
      @input="confirmTrash = $event"
      @confirm="runMoveToTrash"
    >
      <p class="mt-4">
        Move {{ selected.length }} email(s) to <strong>Trash</strong>?
        <span v-if="needsEmailApi('trashBucket')">
          They will no longer show in the message list and cannot be brought back.
        </span>
        <span v-else>
          You can find trashed emails later under the Trash tab.
        </span>
      </p>
      <p v-if="trashOtherMailboxWarning" class="warning--text text--darken-3 font-weight-medium mb-0">
        <v-icon class="mr-1 mt-n1" color="warning darken-3" small>mdi-alert</v-icon>{{ trashOtherMailboxWarning }}
      </p>
    </ActionDialog>

    <ActionDialog
      max-width="500"
      :value="confirmConnect"
      title="Connect emails to firm"
      confirm-text="Connect"
      confirm-color="secondary"
      divider
      close-text="Cancel"
      persistent
      :disabled="connectSubmitting"
      :confirm-loading="connectSubmitting"
      @close="closeConnectDialog"
      @input="onConnectDialogInput"
      @confirm="runConnectToFirm"
    >
      <p class="mt-4 mb-0">
        Connect {{ selected.length }} email(s) as notes (status below).
        email.txt/email.html and attachments are saved on each note.
        <span v-if="trashAfterConnect">
          Fully successful connects go to <strong>Trash</strong>; errors stay on this page.
        </span>
        <span v-else-if="!needsEmailApi('connectedBucket')">
          Emails stay under <strong>Connected</strong>.
        </span>
        <span v-else>
          Emails aren’t moved to trash.
        </span>
      </p>
      <v-autocomplete
        ref="connectFirmField"
        v-model="selectedConnectFirm"
        class="mt-8 pt-2"
        clearable
        dense
        :error="connectFirmError"
        :error-messages="connectFirmError ? connectFirmRequiredMsg : ''"
        hide-details="auto"
        hide-no-data
        hide-selected
        item-text="text"
        return-object
        :disabled="connectSubmitting"
        :items="connectFirmItems"
        :loading="connectFirmSearchLoading"
        label="Search firms or enter FID"
        no-filter
        placeholder="Type FID, sort name, insured, phone…"
        :search-input.sync="connectFirmSearchInput"
        @blur="onConnectFirmFieldBlur"
      />
      <v-select
        v-model="connectStatus"
        class="mt-10 pt-2"
        dense
        hide-details="auto"
        item-text="text"
        item-value="text"
        :disabled="connectSubmitting"
        :items="noteStatusItems"
        label="Note status"
      />
      <template v-slot:leftAction>
        <v-checkbox
          v-model="trashAfterConnect"
          class="mt-0 pt-0 email-bulk-trash-after-connect mr-2"
          dense
          hide-details
          :disabled="connectSubmitting"
          :label="trashAfterConnectLabel"
          @change="onTrashAfterConnectChange"
        />
      </template>
    </ActionDialog>

    <snack
      v-if="$_snack.cmpnt === 'Email'"
      v-model="$_snack.open"
      :color="$_snack.color"
      :msg="$_snack.msg"
    />
    <ErrorDialog ref="errorDialog"/>
  </div>
</template>

<script>
import ActionDialog from '@/components/ActionDialog';
import ErrorDialog from '@/components/ErrorDialog';
import InformationDialogIcon from '@/components/InformationDialogIcon';
import {
  appendTrashAfterConnectSnack,
  connectBatchAllFailDialogOptions,
  connectBatchMixedBannerHint,
  connectBatchProblemMessageIds,
  connectBatchSnackMessage,
  messageIdsForFullConnectSuccess,
  readTrashAfterConnectPref,
  runEmailConnectBatch,
  trashMessagesAfterConnect,
  writeTrashAfterConnectPref,
} from '@/components/search/email/emailConnectCore';
import {
  autoAdvanceNotice,
  browsePosition,
  pickAutoAdvanceNeighbor,
  pickBrowseNeighbor,
  readAutoAdvancePref,
} from '@/components/search/email/emailAutoAdvance';
import { formatEmailListDate } from '@/components/search/email/emailListDate';
import EmailMessage from '@/components/search/email/EmailMessage';
import {
  ALL_PEOPLE_MAILBOX_ID,
  ALL_PEOPLE_MAILBOX_NAME,
  CONNECT_FIRM_REQUIRED_MSG,
  EMAIL_LIST_MIN_WIDTH,
  EMAIL_OPEN_FULL,
  EMAIL_OPEN_PANE,
  EMAIL_OPEN_WINDOW,
  EMAIL_PANE_MIN_WIDTH,
  emailMessageWindowFeatures,
  needsEmailApi,
  otherMailboxTrashWarning,
  pickUsernameMailboxId,
  readEmailOpenModePref,
  readEmailPaneWidthPref,
  TRASH_AFTER_CONNECT_LABEL,
  writeEmailOpenModePref,
  writeEmailPaneWidthPref,
} from '@/components/search/email/emailConstants';
import { FirmsAPIService } from '@/servicehandlers/FirmsAPIService';
import { createNuEmailClient } from '@/servicehandlers/nuEmailApi';

const firmsAPIService = new FirmsAPIService();
const FALLBACK_NOTE_STATUSES = ['archive', 'check', 'hold', 'low', 'normal', 'urgent', 'xtreme'];
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];
const GMAIL_MAX_PAGE_SIZE = 500; // 1 Jul 2026 JCH: Gmail caps messages.list at 500
const FIRM_SEARCH_DEBOUNCE_MS = 280;
const FID_SEARCH_RE = /^\d+$/;

const EMAIL_COL_SELECT = 56;
const EMAIL_COL_TO = 140;
const EMAIL_COL_FROM = 380;
const EMAIL_COL_FROM_WITH_TO = 352;
const EMAIL_COL_DATE = 126;

// 13 Jul 2026 JCH: rows scroll horizontally when narrow (app-wide table convention);
// only the footer, which can't scroll, shortens its copy below this host width.
const EMAIL_FOOTER_COMPACT_MAX = 560;

function firmSearchPayloadForFid(fid) {
  return {
    page: 1,
    perPage: 1,
    sortBy: null,
    sortDesc: false,
    checkedColumns: ['fid', 'sort', 'insured', 'name1', 'name2'],
    whereColumns: { fid },
    includeCheckColumns: {},
    includeColumns: [],
    simple: false,
    getall: false,
  };
}

function firmRowsFromFidResponse(data) {
  if (data && Array.isArray(data.result)) return data.result;
  return Array.isArray(data) ? data : [];
}

function firmSearchItemsFromRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) => {
    const sort = r.sort || '';
    const insured = r.insured || '';
    const fid = r.fid;
    const parts = [sort, insured].filter(Boolean);
    const head = parts.length ? parts.join(' — ') : '';
    const text = head ? `FID ${fid} — ${head}` : `FID ${fid}`; // 1 Jul 2026 JCH: FID first — value used on import
    return { text, fid };
  });
}

export default {
  name: 'Email',
  components: { ActionDialog, EmailMessage, ErrorDialog, InformationDialogIcon },
  data() {
    return {
      client: createNuEmailClient(),
      mailboxes: [],
      mailboxId: null,
      mailboxesLoading: false,
      bucketFilter: 'inbox',
      listItems: [],
      total: 0,
      hasNextPage: false,
      page: 1,
      pageSize: 100,
      loadingList: false,
      selected: [],
      highlightedMessageId: null,
      busy: false,
      connectSubmitting: false,
      confirmTrash: false,
      confirmConnect: false,
      selectedConnectFirm: null,
      connectFirmItems: [],
      connectFirmSearchInput: '',
      connectFirmSearchLoading: false,
      connectFirmSearchTimer: null,
      connectFirmError: false,
      connectFirmRequiredMsg: CONNECT_FIRM_REQUIRED_MSG,
      trashAfterConnectLabel: TRASH_AFTER_CONNECT_LABEL,
      connectStatus: 'normal',
      trashAfterConnect: readTrashAfterConnectPref(),
      batchOutcomeBanner: '',
      openMode: readEmailOpenModePref(),
      paneQuery: null, // null = no email open in full view / side pane
      paneWidth: readEmailPaneWidthPref(), // null = CSS clamp default
      paneDragging: false,
      _paneDragCleanup: null,
      listSearchInput: '',
      listSearchActive: '',
      listSearchTimer: null,
      footerCompact: false, // narrow list — shorter footer copy (footer can't h-scroll)
      _tableResizeObserver: null,
      _tableResizeBound: null,
      _emailRefreshBound: null,
    };
  },
  computed: {
    /** Fill viewport under title/toolbar/footer (PeopleSearch-style calc; JS refines on paint). */
    tableBodyHeight() {
      const chrome = this.batchOutcomeBanner ? 192 : 140;
      return `calc(100vh - var(--offset, 48px) - ${chrome}px)`;
    },
    noteStatusItems() {
      const raw = this.$_lists && this.$_lists.nstatus;
      if (Array.isArray(raw) && raw.length) return raw;
      return FALLBACK_NOTE_STATUSES.map((text) => ({ text }));
    },
    isAllPeopleMailbox() {
      return this.mailboxId === ALL_PEOPLE_MAILBOX_ID;
    },
    mailboxSelectLabel() {
      if (this.mailboxesLoading) return 'Loading mailboxes…';
      if (!this.mailboxId) return 'Select mailbox…';
      return 'Mailbox';
    },
    mailboxItems() { // 1 Jul 2026 JCH: disambiguate duplicate display names via local-part
      const filtered = this.mailboxes.filter((m) => m && m.id !== ALL_PEOPLE_MAILBOX_ID);
      const total = filtered.reduce(
        (sum, m) => sum + (Number.isFinite(m.total) ? m.total : 0),
        0,
      );
      let source = [{
        id: ALL_PEOPLE_MAILBOX_ID,
        name: ALL_PEOPLE_MAILBOX_NAME,
        total: total > 0 ? total : null,
      }, ...filtered];
      const counts = {};
      source.forEach((m) => {
        const n = ((m && m.name) || '').trim();
        if (n) counts[n] = (counts[n] || 0) + 1;
      });
      return source.map((m) => {
        const name = ((m && m.name) || m.id || '').trim();
        const addr = (m && (m.address || m.email || m.id)) || '';
        const local = String(addr).split('@')[0];
        const dup = counts[name] > 1;
        const base = dup && local ? `${name} (${local})` : name;
        const total = m && Number.isFinite(m.total) ? m.total : null; // 1 Jul 2026 JCH: total only — no mark-as-read API
        const label = total != null ? `${base} — ${total.toLocaleString()}` : base;
        return { ...m, label };
      });
    },
    showToColumn() { // 1 Jul 2026 JCH: all-people rows include recipient
      return this.listItems.some((m) => m && m.to && String(m.to).trim());
    },
    headers() {
      const withTo = this.showToColumn;
      const cols = [
        { text: '', value: 'data-table-select', sortable: false, width: '56px', align: 'center' },
        { text: 'To', value: 'to', sortable: false, width: '140px' },
        { text: 'From', value: 'from', sortable: false, width: withTo ? '352px' : '380px' },
        { text: 'Subject', value: 'subject', sortable: false, class: 'email-col-subject', cellClass: 'email-col-subject' },
        { text: 'Date', value: 'received_at', sortable: false, width: '126px', align: 'end' },
      ];
      return withTo ? cols : cols.filter((c) => c.value !== 'to');
    },
    serverSearch() {
      return !needsEmailApi('serverSearch');
    },
    showBuckets() {
      return !needsEmailApi('connectedBucket') || !needsEmailApi('trashBucket');
    },
    pageSizeOptions() {
      return PAGE_SIZE_OPTIONS.filter((n) => n <= GMAIL_MAX_PAGE_SIZE);
    },
    footerProps() { // compact: shorter label so the footer fits without clipping
      return {
        'items-per-page-options': this.pageSizeOptions,
        'items-per-page-text': this.footerCompact ? 'Rows' : 'Rows per page',
      };
    },
    allVisibleSelected() {
      if (!this.visibleItems.length) return false;
      const ids = new Set(this.selected.map((r) => r.id));
      return this.visibleItems.every((r) => ids.has(r.id));
    },
    someVisibleSelected() {
      if (!this.selected.length || this.allVisibleSelected) return false;
      const ids = new Set(this.selected.map((r) => r.id));
      return this.visibleItems.some((r) => ids.has(r.id));
    },
    totalTrusted() { // 1 Jul 2026 JCH: ignore total when it can't cover shown rows + hasNext
      if (!Number.isFinite(this.total) || this.total <= 0) return false;
      const end = (this.page - 1) * this.pageSize + this.listItems.length;
      return !(this.hasNextPage && this.total <= end);
    },
    clientFilterActive() {
      return !this.serverSearch && !!(this.listSearchActive && this.listSearchActive.trim());
    },
    filterHint() {
      if (this.serverSearch) return undefined;
      if (this.clientFilterActive) return `${this.visibleItems.length} match(es) on this page`;
      return `${this.footerRangeText} — filters this page only`;
    },
    visibleItems() {
      if (!this.clientFilterActive) return this.listItems;
      const q = this.listSearchActive.trim().toLowerCase();
      return this.listItems.filter((m) => { // 1 Jul 2026 JCH: match to, from, subject, snippet on this page
        const hay = `${m.to || ''} ${m.from || ''} ${m.subject || ''} ${m.snippet || ''}`.toLowerCase();
        return hay.includes(q);
      });
    },
    tableServerLength() { // 1 Jul 2026 JCH: footer Next from hasNextPage, not estimated total
      return this.hasNextPage
        ? this.page * this.pageSize + 1
        : (this.page - 1) * this.pageSize + this.listItems.length;
    },
    footerRangeText() {
      if (this.clientFilterActive) {
        // Full wording lives in the filter hint; keep the narrow footer short.
        return this.footerCompact
          ? `${this.visibleItems.length} match(es)`
          : `${this.visibleItems.length} match(es) on this page`;
      }
      if (!this.listItems.length) return '0';
      const start = (this.page - 1) * this.pageSize + 1;
      const end = (this.page - 1) * this.pageSize + this.listItems.length;
      return this.totalTrusted ? `${start}–${end} of ${this.total.toLocaleString()}` : `${start}–${end}`;
    },
    canTrashSelection() {
      if (!this.selected.length) return false;
      return !this.selected.some((r) => r.deleted);
    },
    trashOtherMailboxWarning() { // 10 Jul 2026 JCH: cross-mailbox trash also empties the owner's inbox
      return otherMailboxTrashWarning(
        this.selected, this.mailboxes, this.$store.getters.username, this.mailboxId,
      );
    },
    canConnectSelection() {
      if (!this.selected.length) return false;
      if (this.bucketFilter === 'deleted') return false;
      return !this.selected.some((r) => r.connected || r.deleted);
    },
    paneEnabled() { // 13 Jul 2026 JCH: the side-by-side split needs room — when narrow, pane mode presents full width
      return this.openMode === EMAIL_OPEN_PANE && this.$vuetify.breakpoint.mdAndUp;
    },
    viewerOpen() {
      return !!(this.paneQuery && this.openMode !== EMAIL_OPEN_WINDOW);
    },
    fullViewerOpen() {
      return this.viewerOpen && (this.openMode === EMAIL_OPEN_FULL || !this.paneEnabled);
    },
    paneListApi() {
      return { resolveAutoAdvance: (messageId) => this.resolveAutoAdvance(messageId) };
    },
    /** ‹ › position of the pane email among the rows as currently shown/filtered. */
    paneBrowsePos() {
      const id = this.paneQuery && this.paneQuery.message_id;
      return browsePosition(this.visibleItems, id);
    },
    paneStyle() {
      const style = { height: this.tableBodyHeight };
      if (!this.fullViewerOpen && this.paneWidth) style.width = `${this.paneWidth}px`;
      return style;
    },
    /** Phone toggle value: both in-page modes collapse to "page"; window stays. */
    openModeMobile: {
      get() {
        return this.openMode === EMAIL_OPEN_WINDOW ? EMAIL_OPEN_WINDOW : 'page';
      },
      set(v) {
        const next = v === EMAIL_OPEN_WINDOW
          ? EMAIL_OPEN_WINDOW
          // window → page restores the app default; an in-page pref is left as saved
          : (this.openMode === EMAIL_OPEN_WINDOW ? EMAIL_OPEN_PANE : this.openMode);
        this.onOpenModeChange(next);
      },
    },
  },
  watch: {
    selectedConnectFirm(val) {
      if (val) this.connectFirmError = false;
    },
    connectFirmSearchInput(val) {
      if (this.connectFirmError) this.connectFirmError = false;
      this.scheduleConnectFirmSearch(val);
    },
    showToColumn() {
      this.$nextTick(() => this.syncEmailTableLayout());
    },
    footerCompact() {
      this.$nextTick(() => this.syncEmailTableLayout());
    },
    batchOutcomeBanner() {
      this.$nextTick(() => this.syncEmailTableLayout());
    },
    viewerOpen(open) {
      if (open) this.clampPaneWidth(); // before first paint — saved width may not fit this window
      this.$nextTick(() => this.syncEmailTableLayout());
    },
  },
  mounted() {
    if (typeof window !== 'undefined') {
      document.title = 'Email';
      window.__NU_EMAIL_CLIENT__ = this.client; // 10 Jul 2026 JCH: shared with EmailMessage popup via window.opener (also handy in the dev console)
      window.__NU_EMAIL_LIST__ = {
        resolveAutoAdvance: (messageId) => this.resolveAutoAdvance(messageId),
        browsePosition: (messageId) => browsePosition(this.visibleItems, messageId),
        resolveBrowse: (messageId, dir) => this.resolveBrowse(messageId, dir),
      };
      this._emailRefreshBound = this.onEmailRefreshMessage.bind(this);
      window.addEventListener('message', this._emailRefreshBound);
    }
    this.bootstrap();
    this.$nextTick(() => this.setupTableResizeObserver());
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      if (window.__NU_EMAIL_CLIENT__ === this.client) {
        window.__NU_EMAIL_CLIENT__ = undefined;
      }
      if (window.__NU_EMAIL_LIST__ && window.__NU_EMAIL_LIST__.resolveAutoAdvance) {
        window.__NU_EMAIL_LIST__ = undefined;
      }
      if (this._emailRefreshBound) {
        window.removeEventListener('message', this._emailRefreshBound);
      }
    }
    this.clearListSearchTimersOnly();
    this.clearConnectFirmSearchTimer();
    this.teardownTableResizeObserver();
    if (this._paneDragCleanup) this._paneDragCleanup();
  },
  methods: {
    needsEmailApi,

    focusConnectFirmField() {
      this.$nextTick(() => {
        const field = this.$refs.connectFirmField;
        if (field && typeof field.focus === 'function') field.focus();
      });
    },

    requireConnectFirmSelected() {
      const fid = this.resolveConnectFirmId();
      if (Number.isFinite(fid) && fid > 0) return fid;
      this.connectFirmError = true;
      this.snack({ color: 'warning', msg: this.connectFirmRequiredMsg });
      this.focusConnectFirmField();
      return NaN;
    },

    closeConnectDialog() {
      this.confirmConnect = false;
      this.connectFirmError = false;
    },

    onConnectDialogInput(open) {
      this.confirmConnect = open;
      if (!open) this.connectFirmError = false;
    },

    onEmailRefreshMessage(ev) {
      if (!ev || ev.origin !== window.location.origin) return;
      if (ev.data && ev.data.type === 'email-refresh') { // 1 Jul 2026 JCH: popup finished connect/trash — refresh list
        this.handleEmailRefresh(ev.data);
      }
    },

    /** Shared by the popup postMessage path and the reading pane's refresh event. */
    handleEmailRefresh(data = {}) {
      this.selected = [];
      if (data.highlightId) this.highlightedMessageId = data.highlightId;
      if (this.page > 1 && !data.keepPage) this.page = 1;
      if (data.endNotice) this.snack({ msg: data.endNotice });
      Promise.all([this.loadMessages(), this.refreshMailboxCounts()]);
    },

    /** Popup asks before list refresh so neighbor is still on this page. */
    resolveAutoAdvance(messageId) {
      const pref = readAutoAdvancePref();
      if (pref === 'off' || pref === 'list') return { skipped: true };
      const pick = pickAutoAdvanceNeighbor(this.visibleItems, messageId, pref);
      if (pick.end || !pick.item) {
        return {
          end: true,
          notice: autoAdvanceNotice(pick),
          hasMoreListPages: !!this.hasNextPage,
        };
      }
      return {
        next: this.emailMessageQuery(pick.item),
        nextId: pick.item.id,
        notice: autoAdvanceNotice(pick),
        openedAs: pick.openedAs || null,
        usedFallback: !!pick.usedFallback,
      };
    },

    snack(payload) {
      this.$store.dispatch('SET_SNACK', { cmpnt: 'Email', open: true, ...payload });
    },

    clearSelection() {
      this.selected = [];
    },

    clearBatchOutcomeBanner() {
      this.batchOutcomeBanner = '';
    },

    /** After mixed bulk connect: keep partial/hard-fail rows selected once the list refreshes. */
    reselectProblemRows(problemIds) {
      const ids = problemIds instanceof Set ? problemIds : new Set(problemIds || []);
      if (!ids.size) {
        this.selected = [];
        return;
      }
      this.selected = this.listItems.filter((r) => r && ids.has(r.id));
    },

    toggleSelectAll(val) { // 1 Jul 2026 JCH: all visible rows on current page
      this.selected = val ? [...this.visibleItems] : [];
    },

    defaultPersonMailboxId() {
      return pickUsernameMailboxId(this.mailboxes, this.$store.getters.username);
    },

    messageMailboxId(row) {
      if (row && row.mailbox_id) return row.mailbox_id;
      return this.mailboxId;
    },

    groupSelectedByMailbox(rows) {
      const groups = new Map();
      (rows || []).forEach((r) => {
        const mb = this.messageMailboxId(r);
        if (!mb) return;
        if (!groups.has(mb)) groups.set(mb, []);
        groups.get(mb).push(r.id);
      });
      return groups;
    },

    async softDeleteGrouped(rows) { // 1 Jul 2026 JCH: all-people — trash per row mailbox_id
      const groups = this.groupSelectedByMailbox(rows);
      let moved = 0;
      for (const [mb, ids] of groups) {
        // eslint-disable-next-line no-await-in-loop
        const result = await this.client.softDeleteMessages(mb, ids);
        const n = result && Array.isArray(result.moved) ? result.moved.length : ids.length;
        moved += n;
      }
      return moved;
    },

    formatEmailListDate,

    mailboxDisplayName() {
      const m = this.mailboxes.find((x) => x.id === this.mailboxId);
      return (m && m.name) || this.mailboxId || '';
    },

    async bootstrap() {
      await this.refreshMailboxes();
      this.$nextTick(() => this.syncEmailTableLayout());
    },

    async refreshMailboxCounts() {
      try {
        this.mailboxes = await this.client.listMailboxes();
      } catch (e) {
        // keep existing counts — list refresh still runs
      }
    },

    async refreshMailboxes() {
      this.mailboxesLoading = true;
      this.loadingList = true; // 6 May 2026 JCH: avoid "No data available" flash while mailboxes load
      try {
        this.mailboxes = await this.client.listMailboxes();
        if (!this.mailboxId && this.mailboxes.length) {
          this.mailboxId = this.defaultPersonMailboxId();
        }
        await this.loadMessages();
      } catch (e) {
        this.snack({ color: 'error', msg: 'Failed to load mailboxes.' });
      } finally {
        this.mailboxesLoading = false;
        if (!this.mailboxId) this.loadingList = false;
      }
    },

    onMailboxChange() {
      this.clearListSearchTimersOnly();
      this.listSearchInput = '';
      this.listSearchActive = '';
      this.highlightedMessageId = null;
      this.selected = [];
      this.clearBatchOutcomeBanner();
      this.closePane();
      this.page = 1;
      this.bucketFilter = 'inbox';
      this.loadMessages();
    },

    onBucketFilterChange() {
      this.page = 1;
      this.selected = [];
      this.highlightedMessageId = null;
      this.clearBatchOutcomeBanner();
      this.closePane();
      this.total = 0;
      this.loadMessages();
    },

    clearListSearchTimersOnly() {
      if (this.listSearchTimer) {
        clearTimeout(this.listSearchTimer);
        this.listSearchTimer = null;
      }
    },

    clearConnectFirmSearchTimer() {
      if (this.connectFirmSearchTimer) {
        clearTimeout(this.connectFirmSearchTimer);
        this.connectFirmSearchTimer = null;
      }
    },

    scheduleConnectFirmSearch(q) {
      this.clearConnectFirmSearchTimer();
      this.connectFirmSearchTimer = setTimeout(() => this.fetchConnectFirms(q), FIRM_SEARCH_DEBOUNCE_MS);
    },

    /** Blur (e.g. clicking Note status) wipes uncommitted text and would cancel the
     * debounced lookup — flush it now so a just-typed FID still auto-selects. */
    onConnectFirmFieldBlur() {
      if (!this.connectFirmSearchTimer) return;
      this.clearConnectFirmSearchTimer();
      this.fetchConnectFirms(this.connectFirmSearchInput);
    },

    async fetchConnectFirms(q) {
      const s = (q == null ? '' : String(q)).trim();
      if (!s) { // blur artifact — an emptied box must not drop a made selection
        if (!this.selectedConnectFirm) this.connectFirmItems = [];
        this.connectFirmSearchLoading = false;
        return;
      }
      if (this.selectedConnectFirm && s !== this.selectedConnectFirm.text && s !== String(this.selectedConnectFirm.fid)) {
        this.selectedConnectFirm = null;
      }
      if (this.selectedConnectFirm && s === this.selectedConnectFirm.text) {
        this.connectFirmSearchLoading = false;
        return;
      }
      const isFidSearch = FID_SEARCH_RE.test(s);
      if (!isFidSearch && s.length < 2) {
        this.connectFirmItems = [];
        this.connectFirmSearchLoading = false;
        return;
      }
      this.connectFirmSearchLoading = true;
      try {
        const data = isFidSearch
          ? await firmsAPIService.getFirm(firmSearchPayloadForFid(Number(s)), this.$router)
          : await firmsAPIService.getUnisearchFirm({ search: s, limit: 50 }, this.$router);
        const rows = isFidSearch ? firmRowsFromFidResponse(data) : (Array.isArray(data) ? data : []);
        const items = firmSearchItemsFromRows(rows);
        this.connectFirmItems = items;
        if (isFidSearch && items.length === 1 && Number(items[0].fid) === Number(s)) {
          this.selectedConnectFirm = items[0];
        }
      } catch (e) {
        this.connectFirmItems = [];
      } finally {
        this.connectFirmSearchLoading = false;
      }
    },

    resolveConnectFirmId() {
      if (this.selectedConnectFirm && this.selectedConnectFirm.fid !== undefined && this.selectedConnectFirm.fid !== null) {
        const n = Number(this.selectedConnectFirm.fid);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return NaN;
    },

    scheduleListSearch() {
      this.clearListSearchTimersOnly();
      this.listSearchTimer = setTimeout(() => {
        const next = (this.listSearchInput || '').trim();
        if (next === this.listSearchActive) return;
        this.listSearchActive = next;
        if (!this.serverSearch) return; // 1 Jul 2026 JCH: client filter — no server round-trip
        this.page = 1;
        this.loadMessages();
      }, 300);
    },

    clearListSearch() {
      this.clearListSearchTimersOnly();
      this.listSearchInput = '';
      this.listSearchActive = '';
      if (!this.serverSearch) return;
      this.page = 1;
      this.loadMessages();
    },

    onPageChange(p) {
      this.page = p;
      this.loadMessages();
    },

    itemRowClass(row) {
      if (!row) return '';
      const bits = [];
      if (this.highlightedMessageId && row.id === this.highlightedMessageId) bits.push('email-row-active');
      return bits.join(' ');
    },

    async loadMessages() {
      if (!this.mailboxId) {
        this.listItems = [];
        this.total = 0;
        return;
      }
      this.loadingList = true;
      try {
        const { items, total, hasNext } = await this.client.listMessages({
          mailboxId: this.mailboxId,
          page: this.page,
          pageSize: this.pageSize,
          search: this.serverSearch ? (this.listSearchActive || null) : null,
          bucketFilter: this.bucketFilter,
        });
        this.total = total;
        this.hasNextPage = hasNext !== undefined ? !!hasNext : this.page * this.pageSize < total;
        this.listItems = items;
        if (this.highlightedMessageId && !items.some((r) => r.id === this.highlightedMessageId)) {
          this.highlightedMessageId = null;
        }
      } catch (e) {
        this.snack({ color: 'error', msg: 'Failed to load emails.' });
      } finally {
        this.loadingList = false;
        this.$nextTick(() => this.syncEmailTableLayout());
      }
    },

    onItemsPerPageChange(v) {
      this.pageSize = v;
      this.page = 1;
      this.loadMessages();
    },

    openConnectDialogMulti() {
      if (!this.noteStatusItems.some((i) => i.text === this.connectStatus)) {
        this.connectStatus = (this.noteStatusItems.find((i) => i.text === 'normal') || this.noteStatusItems[0]).text;
      }
      this.connectFirmError = false;
      this.confirmConnect = true;
    },

    onBulkConnectClick() {
      if (!this.canConnectSelection) {
        if (!this.selected.length) return;
        if (this.bucketFilter === 'deleted') {
          this.snack({ color: 'warning', msg: 'Cannot connect emails in Trash.' });
          return;
        }
        if (this.selected.some((r) => r.connected)) {
          const msg = needsEmailApi('connectedBucket')
            ? 'Deselect emails that are already connected.'
            : 'Deselect emails already in Connected — or open the Connected tab.';
          this.snack({ color: 'warning', msg });
          return;
        }
        return;
      }
      if (this.selected.length === 1) { // 6 May 2026 JCH: one email → detail view for staff/attachments
        this.openMessageWindow(this.selected[0]);
        return;
      }
      this.openConnectDialogMulti();
    },

    onTrashAfterConnectChange(val) {
      writeTrashAfterConnectPref(!!val);
    },

    async runConnectToFirm() {
      const fid = this.requireConnectFirmSelected();
      if (!Number.isFinite(fid) || fid <= 0) return;
      const username = this.$store.getters.username;
      if (!username) {
        this.snack({ color: 'error', msg: 'Not signed in — cannot connect emails.' });
        return;
      }

      const rows = [...this.selected];
      if (!rows.length || !this.mailboxId) {
        this.confirmConnect = false;
        return;
      }

      this.connectSubmitting = true;
      this.busy = true;
      const mailboxLabel = this.mailboxDisplayName();

      try {
        const batch = await runEmailConnectBatch({
          client: this.client,
          router: this.$router,
          username,
          mailboxLabel,
          mailboxId: this.mailboxId,
          messageRows: rows,
          fid,
          status: this.connectStatus,
        });

        this.confirmConnect = false;
        this.highlightedMessageId = null;
        const problemIds = new Set(connectBatchProblemMessageIds(batch.results));

        let trashOutcome = { trashed: 0, failed: 0 };
        if (this.trashAfterConnect) {
          const successIds = new Set(messageIdsForFullConnectSuccess(batch.results));
          const successRows = rows.filter((r) => successIds.has(r.id));
          if (successRows.length) {
            if (this.isAllPeopleMailbox) {
              trashOutcome = { trashed: await this.softDeleteGrouped(successRows), failed: 0 };
            } else {
              trashOutcome = await trashMessagesAfterConnect(this.client, this.mailboxId, [...successIds]);
            }
          }
        }

        if (trashOutcome.trashed && this.page > 1) this.page = 1;
        const refreshes = [this.loadMessages()];
        if (trashOutcome.trashed) refreshes.push(this.refreshMailboxCounts());
        await Promise.all(refreshes);

        // 11 Jul 2026 JCH: all hard-fail → ErrorDialog only; mixed keeps problem rows + sticky banner
        if (batch.failCount && !(batch.importedNotes || []).length) {
          this.reselectProblemRows(problemIds);
          this.clearBatchOutcomeBanner();
          if (this.$refs.errorDialog) {
            this.$refs.errorDialog.showError(connectBatchAllFailDialogOptions(batch));
          }
          return;
        }

        const snackBase = connectBatchSnackMessage({
          ...batch,
          fid,
          trashed: trashOutcome.trashed,
        });
        if (snackBase.mixed && problemIds.size) {
          this.batchOutcomeBanner = connectBatchMixedBannerHint(batch);
          this.reselectProblemRows(problemIds);
        } else {
          this.clearBatchOutcomeBanner();
          this.selected = [];
        }
        this.snack(appendTrashAfterConnectSnack(snackBase, trashOutcome));
      } finally {
        this.connectSubmitting = false;
        this.busy = false;
      }
    },

    onRowClick(evt, { item }) {
      if (evt && evt.target && evt.target.closest && evt.target.closest('.v-input--selection-controls')) {
        return;
      }
      if (item) this.openMessageWindow(item);
    },

    emailMessageQuery(item) {
      const query = {
        mailbox_id: this.messageMailboxId(item),
        message_id: item.id,
      };
      if (item && item.attachment_count) query.listed_attach = '1'; // 1 Jul 2026 JCH: list paperclip — detail may differ
      return query;
    },

    openMessageWindow(item) {
      if (!item || !this.messageMailboxId(item)) return;
      this.highlightedMessageId = item.id;
      const query = this.emailMessageQuery(item);
      if (this.openMode !== EMAIL_OPEN_WINDOW) { // pane (full width when narrow) or full view
        this.paneQuery = query;
        return;
      }
      this.openInWindow(query);
    },

    openInWindow(query) {
      const resolved = this.$router.resolve({ name: 'EmailMessage', query });
      const url = resolved.href.startsWith('http') ? resolved.href : `${window.location.origin}${resolved.href}`;
      const features = emailMessageWindowFeatures();
      window.open(url, '_blank', features);
    },

    /** Switching modes re-hosts the open email in place (window mode pops it out).
     * Nothing open but a row highlighted (green) → open that row in the new mode. */
    onOpenModeChange(mode) {
      writeEmailOpenModePref(mode);
      this.openMode = readEmailOpenModePref();
      if (this.openMode === EMAIL_OPEN_WINDOW && this.paneQuery) {
        const query = this.paneQuery;
        this.closePane();
        this.openInWindow(query);
        return;
      }
      this.reopenHighlightedRow();
    },

    /** Toggle only emits change on a new value — re-clicking the active icon
     * should still open the highlighted row (e.g. full view closed, mode still full). */
    onModeIconClick(mode) {
      if (mode !== this.openMode) return; // new value — the change handler takes it
      this.reopenHighlightedRow();
    },

    /** Same re-click behavior for the phone toggle's in-page button. */
    onMobilePageIconClick() {
      if (this.openMode === EMAIL_OPEN_WINDOW) return; // switching — the setter takes it
      this.reopenHighlightedRow();
    },

    /** Green row + mode icon (any of the three) = open that row in that mode. */
    reopenHighlightedRow() {
      if (this.paneQuery || !this.highlightedMessageId) return;
      const row = this.listItems.find((r) => r && r.id === this.highlightedMessageId);
      if (row) this.openMessageWindow(row);
    },

    closePane() {
      this.paneQuery = null;
    },

    /** Grab bar: mouse-drag trades list width for pane width; saved on release. */
    onPaneDividerDown(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      const pane = this.$refs.paneHost;
      const split = this.$refs.splitHost;
      if (!pane || !split) return;
      const startX = e.clientX;
      const startW = pane.getBoundingClientRect().width;
      // Distributable width = list + pane (split padding and divider excluded).
      const host = this.$refs.tableHost;
      const hostW = host ? host.getBoundingClientRect().width : 0;
      const maxW = Math.max(EMAIL_PANE_MIN_WIDTH, startW + hostW - EMAIL_LIST_MIN_WIDTH);
      this.paneDragging = true;
      let raf = null;
      const onMove = (ev) => {
        const w = Math.min(maxW, Math.max(EMAIL_PANE_MIN_WIDTH, startW + (startX - ev.clientX)));
        this.paneWidth = Math.round(w);
        if (raf === null && typeof requestAnimationFrame !== 'undefined') {
          raf = requestAnimationFrame(() => {
            raf = null;
            this.syncEmailTableColWidths(); // fixed columns re-pin as the list narrows
          });
        }
      };
      const cleanup = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        this.paneDragging = false;
        this._paneDragCleanup = null;
      };
      const onUp = () => {
        cleanup();
        writeEmailPaneWidthPref(this.paneWidth);
        this.$nextTick(() => this.syncEmailTableLayout());
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      this._paneDragCleanup = cleanup;
    },

    /** A width saved on a larger window must not squeeze the list below its minimum.
     * In-memory only — the saved pref still applies in full on the bigger monitor. */
    clampPaneWidth() {
      if (!this.paneWidth) return;
      const host = this.$refs.tableHost;
      if (!host) return;
      const hostW = host.getBoundingClientRect().width;
      if (!hostW) return; // hidden (full view) — re-clamped on next open/resize
      const pane = this.$refs.paneHost;
      // Pane not mounted yet (pre-open clamp): the host still spans the split, so
      // subtract the divider it's about to give up; otherwise sum the two panes.
      const avail = pane ? hostW + pane.getBoundingClientRect().width : hostW - 8;
      const maxW = Math.max(EMAIL_PANE_MIN_WIDTH, avail - EMAIL_LIST_MIN_WIDTH);
      if (this.paneWidth > maxW) this.paneWidth = Math.round(maxW);
    },

    /** Double-click the grab bar: back to the default pane width. */
    resetPaneWidth() {
      this.paneWidth = null;
      writeEmailPaneWidthPref(null);
      this.$nextTick(() => this.syncEmailTableLayout());
    },

    /** Pane header action: continue this email in the usual popup window. */
    movePaneToWindow() {
      const query = this.paneQuery;
      this.closePane();
      if (query) this.openInWindow(query);
    },

    /** ‹ › in the pane bar — open the adjacent row in the same viewer. */
    browsePane(dir) {
      const id = this.paneQuery && this.paneQuery.message_id;
      const item = pickBrowseNeighbor(this.visibleItems, id, dir);
      if (item) this.openMessageWindow(item);
    },

    /** ‹ › from the popup window — hand back the neighbor and move the green row. */
    resolveBrowse(messageId, dir) {
      const item = pickBrowseNeighbor(this.visibleItems, messageId, dir);
      if (!item) return null;
      this.highlightedMessageId = item.id;
      return { next: this.emailMessageQuery(item), nextId: item.id };
    },

    /** Auto-advance inside the pane — swap to the neighbor the list picked. */
    onPaneAdvance(nextQuery) {
      if (!nextQuery || !nextQuery.message_id) return;
      this.highlightedMessageId = nextQuery.message_id;
      this.paneQuery = { ...nextQuery };
    },

    trashSingle(item) { // 6 May 2026 JCH: reuse bulk trash confirm for one row
      if (!item || item.deleted) return;
      this.selected = [item];
      this.confirmTrash = true;
    },

    async runMoveToTrash() {
      const ids = this.selected.map((r) => r.id);
      if (!ids.length || !this.mailboxId) {
        this.confirmTrash = false;
        return;
      }
      this.busy = true;
      try {
        const moved = await this.softDeleteGrouped(this.selected);
        this.snack({ msg: `Moved ${moved} email(s) to Trash.` });
        this.selected = [];
        this.highlightedMessageId = null;
        this.confirmTrash = false;
        if (moved && this.page > 1) this.page = 1;
        await Promise.all([this.loadMessages(), this.refreshMailboxCounts()]);
      } catch (e) {
        this.snack({ color: 'error', msg: 'Could not move to Trash.' });
      } finally {
        this.busy = false;
      }
    },

    setupTableResizeObserver() {
      const el = this.$refs.tableHost;
      if (!el) return;
      if (typeof ResizeObserver !== 'undefined') {
        // Defer to next frame — mutating layout inside the observer callback
        // triggers the "ResizeObserver loop" dev-overlay error.
        this._tableResizeObserver = new ResizeObserver(() => {
          if (typeof requestAnimationFrame !== 'undefined') {
            requestAnimationFrame(() => this.syncEmailTableLayout());
          } else {
            this.syncEmailTableLayout();
          }
        });
        this._tableResizeObserver.observe(el);
      }
      if (typeof window !== 'undefined') {
        this._tableResizeBound = () => {
          this.clampPaneWidth(); // shrinking the window mustn't squeeze the list
          this.syncEmailTableLayout();
        };
        window.addEventListener('resize', this._tableResizeBound);
      }
      this.syncEmailTableLayout();
      // Layout often settles after mailbox/list paint.
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => {
          this.syncEmailTableLayout();
          requestAnimationFrame(() => this.syncEmailTableLayout());
        });
      }
      setTimeout(() => this.syncEmailTableLayout(), 0);
      setTimeout(() => this.syncEmailTableLayout(), 250);
    },

    teardownTableResizeObserver() {
      if (typeof window !== 'undefined' && this._tableResizeBound) {
        window.removeEventListener('resize', this._tableResizeBound);
        this._tableResizeBound = null;
      }
      if (this._tableResizeObserver) {
        this._tableResizeObserver.disconnect();
        this._tableResizeObserver = null;
      }
    },

    syncEmailTableLayout() {
      this.updateFooterCompact();
      this.forceTableWrapperHeight();
      this.syncEmailTableColWidths();
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => this.syncEmailTableColWidths());
      }
    },

    /** Rows h-scroll when narrow; the footer can't, so shorten its copy instead. */
    updateFooterCompact() {
      const host = this.$refs.tableHost;
      if (!host) return;
      const w = host.getBoundingClientRect().width;
      if (w > 0) this.footerCompact = w < EMAIL_FOOTER_COMPACT_MAX; // 0 = hidden (full view) — keep last
    },

    /** Belt-and-suspenders: Vuetify height + CSS can both miss; set !important on the real wrapper. */
    forceTableWrapperHeight() {
      const host = this.$refs.tableHost;
      if (!host || typeof window === 'undefined') return;
      const wrapper = host.querySelector('.v-data-table__wrapper');
      if (!wrapper) return;
      const footer = host.querySelector('.v-data-footer');
      const footerH = footer ? footer.getBoundingClientRect().height : 52;
      const top = wrapper.getBoundingClientRect().top;
      const next = Math.max(200, Math.floor(window.innerHeight - top - footerH - 4));
      const px = `${next}px`;
      // No-op guard breaks the observer→write→observer resize loop.
      if (wrapper.style.height === px && wrapper.style.maxHeight === px) return;
      wrapper.style.setProperty('height', px, 'important');
      wrapper.style.setProperty('max-height', px, 'important');
    },

    syncEmailTableColWidths() {
      const host = this.$refs.tableHost;
      if (!host) return;
      const table = host.querySelector('table');
      if (!table) return;
      const cols = table.querySelectorAll('colgroup col');
      if (!cols.length) return;
      const withTo = this.showToColumn;
      const subjectIdx = withTo ? 3 : 2;
      const pins = withTo
        ? [[0, EMAIL_COL_SELECT], [1, EMAIL_COL_TO], [2, EMAIL_COL_FROM_WITH_TO], [4, EMAIL_COL_DATE]]
        : [[0, EMAIL_COL_SELECT], [1, EMAIL_COL_FROM], [3, EMAIL_COL_DATE]];
      cols.forEach((col, i) => {
        if (i === subjectIdx) {
          col.style.removeProperty('width');
          return;
        }
        const pin = pins.find(([idx]) => idx === i);
        if (pin) col.style.width = `${pin[1]}px`;
      });
    },
  },
};
</script>

<style scoped>
.email-info-dialog {
  color: rgba(0, 0, 0, 0.87);
  font-size: 1.0625rem;
  line-height: 1.55;
}

.email-info-dialog p:not(:last-child) {
  margin-bottom: 0.875rem;
}

@media (max-width: 599px) {
  /* 13 Jul 2026 JCH: copy grew (open modes, ‹ ›, grab bar) — one notch smaller so an
     iPhone SE-size screen still shows the whole dialog without scrolling. */
  .email-info-dialog {
    font-size: 0.6875rem; /* 11px */
    line-height: 1.35;
  }

  .email-info-dialog p:not(:last-child) {
    margin-bottom: 0.625rem;
  }
}

/* 11 Jul 2026 JCH: no flex-fill chain — card/host never stretched and overflow:hidden
   clipped the table at mid-screen. Wrapper height is set directly (calc + JS), content flows. */
.email-page {
  --email-scrollbar-gutter: 16px;
  box-sizing: border-box;
  min-height: calc(100vh - var(--offset));
}

.email-toolbar-text {
  overflow: visible;
}

.email-table-host {
  min-width: 0;
}

/* 13 Jul 2026 JCH: reading pane — message list keeps the leftover width. */
.email-split {
  min-width: 0;
}

.email-reading-pane {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  min-width: 0;
  overflow: hidden;
  width: clamp(420px, 44vw, 760px);
}

/* 13 Jul 2026 JCH: Gmail-style full view — replaces the message list, back arrow returns. */
.email-full-view {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  min-width: 0;
  overflow: hidden;
}

.email-back-to-list-btn {
  letter-spacing: normal;
  text-transform: none;
}

.email-open-mode-toggle .v-btn {
  min-width: 34px !important;
  padding: 0 6px !important;
}

/* 13 Jul 2026 JCH: grab bar between list and pane — drag to resize, double-click to reset. */
.email-split-divider {
  border-radius: 2px;
  cursor: col-resize;
  width: 8px;
  background: linear-gradient(rgba(0, 0, 0, 0.22), rgba(0, 0, 0, 0.22)) no-repeat center / 2px 44px;
}

.email-split-divider:hover,
.email-page--pane-dragging .email-split-divider {
  background:
    linear-gradient(#7a9e5f, #7a9e5f) no-repeat center / 2px 72px,
    rgba(156, 190, 126, 0.16);
}

.email-page--pane-dragging {
  cursor: col-resize;
  user-select: none;
}

/* Email body iframe swallows mousemove — mute it while dragging the divider. */
.email-page--pane-dragging .email-reading-pane-body >>> iframe {
  pointer-events: none;
}

.email-reading-pane-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  min-height: 32px;
}

.email-reading-pane-body {
  min-height: 0;
  overflow-y: auto;
}

.email-card-title-row {
  column-gap: 4px;
  display: flex;
  flex-wrap: nowrap;
  min-width: 0;
  overflow-x: auto;
  padding: 2px 12px 6px;
}

@media (max-width: 599px) {
  .email-card-title-row {
    flex-wrap: wrap;
    overflow-x: visible;
    row-gap: 6px;
  }
}

/* 13 Jul 2026 JCH: grow from a 280px basis to 460px cap — wrap bases on flex-basis, so a
   fixed 460 stacked the row long before space actually ran out. One line down to ~600px. */
.email-mailbox-toolbar {
  flex: 1 1 280px;
  max-width: 460px;
  min-width: 0;
}

.email-title-spacer {
  flex: 1 1 auto;
}

@media (max-width: 599px) {
  .email-mailbox-toolbar {
    flex: 1 1 100%;
    max-width: none;
    min-width: 0;
    width: 100%;
  }

}

.email-bulk-mobile {
  border: 1px solid rgba(0, 0, 0, 0.08);
  max-width: min(280px, calc(100vw - 120px));
  min-height: 36px;
  padding: 0 4px;
  transform: translateY(-1px);
}

.email-batch-outcome-inner {
  border: 1px solid rgba(251, 140, 0, 0.45);
}

.email-bulk-mobile-count {
  font-size: 0.875rem;
  line-height: 1;
  margin-right: 6px;
  padding-left: 8px;
}

.email-bulk-mobile-actions {
  margin-left: 0;
}

.email-bulk-mobile-actions >>> .v-btn.email-bulk-action-btn {
  transform: translateY(-1px);
}

.email-bulk-mobile-actions >>> .v-btn.email-bulk-clear-btn {
  transform: translateY(1px);
}

.email-mailbox-select {
  flex-shrink: 0;
  margin-top: 0;
  width: 100%;
}

.email-mailbox-select >>> .v-label.v-label--active {
  transform: translateY(calc(-18px + 4px)) scale(0.75) !important;
}

@media (max-width: 599px) {
  .email-bulk-mobile-count-label {
    display: none;
  }
}

.email-toolbar-single {
  column-gap: 12px;
  row-gap: 8px;
}

.email-buckets {
  column-gap: 10px;
  row-gap: 6px;
}

.email-buckets--title {
  flex-shrink: 0;
  justify-content: flex-end;
  min-height: 40px;
  min-width: 0;
}

.email-search-wrap {
  flex: 1 1 160px;
  min-height: 36px;
  min-width: 0;
}

.email-search-field--toolbar {
  flex: 0 0 auto;
  max-width: none;
  min-width: 0;
  width: 100%;
}

.email-search-wrap >>> .v-text-field .v-input__slot {
  margin-bottom: 0;
}

.email-search-field--toolbar >>> .v-text-field__details {
  margin-top: 2px;
  margin-bottom: 0;
  min-height: 0;
}

.email-table >>> thead th,
.email-table >>> tbody td {
  line-height: 1.3; /* 11 Jul 2026 JCH: 1.0 clipped descenders (g/y/p) under overflow:hidden */
  vertical-align: middle;
}

.email-table >>> .v-data-table__wrapper {
  scrollbar-gutter: stable;
}

/* 13 Jul 2026 JCH: like the rest of the app, narrow hosts scroll rows horizontally —
   min-width keeps Subject readable instead of collapsing it to fit. */
.email-table >>> table {
  min-width: 900px;
  table-layout: fixed;
  width: 100%;
}

.email-table.email-table--with-to >>> table {
  min-width: 1010px;
}

.email-table >>> tbody tr.v-data-table__empty-wrapper td:first-child {
  height: auto !important;
  padding-top: 28px !important;
  padding-bottom: 20px !important;
}

/* 6 May 2026 JCH: override Vuetify data-table-select inline 1px width (clips select-all on narrow screens). */
.email-table >>> thead th:first-child,
.email-table >>> tbody td:first-child {
  box-sizing: border-box;
  max-width: none !important;
  min-width: 56px !important;
  overflow: visible;
  padding: 0 8px 0 10px !important;
  width: 56px !important;
}

.email-table >>> thead > tr > th {
  height: auto !important;
  min-height: 40px;
}

.email-table >>> thead th:not(:first-child),
.email-table >>> tbody td:not(:first-child) {
  overflow-wrap: anywhere;
  white-space: normal;
  word-break: break-word;
}

.email-table >>> tbody > tr > td {
  height: auto !important;
  min-height: 42px;
}

.email-table >>> .v-data-footer {
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.email-table >>> .v-data-footer__select {
  flex: 0 1 auto;
  margin-right: 12px;
  min-width: 0;
}

.email-table >>> .v-data-footer__select .v-select {
  margin-left: 12px;
  width: 54px;
}

.email-table >>> .v-data-footer__pagination {
  flex: 0 0 auto;
  margin: 0 12px;
  min-width: 116px;
  text-align: center;
}

.email-table >>> .v-data-footer__icons-before,
.email-table >>> .v-data-footer__icons-after {
  flex: 0 0 auto;
}

@media (max-width: 599px) {
  .email-table >>> .v-data-footer {
    padding: 0 4px;
  }

  .email-table >>> .v-data-footer__select {
    margin-right: 6px;
  }

  .email-table >>> .v-data-footer__select .v-select {
    margin-left: 6px;
    width: 46px;
  }

  .email-table >>> .v-data-footer__pagination {
    margin: 0 6px;
    min-width: 108px;
  }

  .email-table >>> .v-data-footer .v-btn {
    height: 28px;
    width: 28px;
  }
}

/* 13 Jul 2026 JCH: narrow list — tighter footer that fits (it can't h-scroll with
   the rows); right-justified overflow was clipping "Rows per page" off the left. */
.email-table.email-table--footer-compact >>> .v-data-footer {
  padding: 0 4px;
}

.email-table.email-table--footer-compact >>> .v-data-footer__select {
  margin-right: 6px;
}

.email-table.email-table--footer-compact >>> .v-data-footer__select .v-select {
  margin-left: 6px;
  width: 54px; /* three digits — "100" clipped to "10" at 46px */
}

.email-table.email-table--footer-compact >>> .v-data-footer__pagination {
  margin: 0 6px;
  min-width: 0;
}

.email-table.email-table--footer-compact >>> .v-data-footer .v-btn {
  height: 28px;
  width: 28px;
}

.email-table >>> tbody tr {
  cursor: pointer;
}

.email-table >>> tbody td.email-col-subject,
.email-table >>> thead th.email-col-subject {
  max-width: 0;
  overflow: hidden;
  padding-left: 8px !important;
  padding-right: 4px !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: auto !important;
}

.email-table >>> thead th:last-child,
.email-table >>> tbody td:last-child {
  box-sizing: border-box;
  max-width: 126px !important;
  min-width: 126px !important;
  padding-left: 4px !important;
  padding-right: 8px !important;
  width: 126px !important;
}

.email-table:not(.email-table--with-to) >>> thead th:nth-child(2),
.email-table:not(.email-table--with-to) >>> tbody td:nth-child(2) {
  box-sizing: border-box;
  max-width: 380px !important;
  min-width: 380px !important;
  width: 380px !important;
}

.email-table.email-table--with-to >>> thead th:nth-child(2),
.email-table.email-table--with-to >>> tbody td:nth-child(2) {
  box-sizing: border-box;
  max-width: 140px !important;
  min-width: 140px !important;
  width: 140px !important;
}

.email-table.email-table--with-to >>> thead th:nth-child(3),
.email-table.email-table--with-to >>> tbody td:nth-child(3) {
  box-sizing: border-box;
  max-width: 352px !important;
  min-width: 352px !important;
  width: 352px !important;
}

.email-subject-line {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.email-snippet {
  font-weight: 400;
}

.email-date-wrap {
  min-height: 28px;
  padding-right: var(--email-scrollbar-gutter, 16px);
  position: relative;
}

.email-row-actions {
  align-items: center;
  display: none;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
}

.email-row-actions >>> .v-icon {
  font-size: 20px !important;
}

.email-bulk-mobile-actions >>> .v-icon {
  font-size: 20px !important;
}

.email-connect-icon-btn:not(.v-btn--disabled) {
  color: rgba(0, 0, 0, 0.67) !important;
}

.email-table >>> tbody tr:hover .email-row-actions {
  display: inline-flex;
}

.email-table >>> tbody tr:hover .email-date-wrap--actions .email-date-text {
  visibility: hidden;
}

.email-table--noheader >>> thead > tr:not(.v-data-table__progress) > th {
  border-bottom: none !important;
  height: 0 !important;
  line-height: 0 !important;
  min-height: 0 !important;
  padding-bottom: 0 !important;
  padding-top: 0 !important;
}

.email-table--noheader >>> thead > tr:not(.v-data-table__progress) > th > * {
  display: none !important;
}

.email-selectall {
  box-sizing: border-box;
  flex-shrink: 0;
  justify-content: center;
  min-height: 40px;
  padding: 0 8px 0 10px;
  width: 56px;
}

@media (max-width: 599px) {
  /* Keep the checkbox on the filter's row so it doesn't waste a line when the toolbar stacks. */
  .email-selectall {
    order: 1;
  }

  .email-search-wrap {
    order: 2;
  }
}

.email-table--gmail >>> tbody tr.v-data-table__selected {
  background-color: rgba(0, 0, 0, 0.055) !important;
}

.email-table >>> tr.email-row-active {
  background-color: rgba(156, 190, 126, 0.28) !important;
  box-shadow: inset 3px 0 0 #9CBE7E;
}

.email-table--gmail >>> tbody tr.email-row-active.v-data-table__selected {
  background-color: rgba(156, 190, 126, 0.36) !important;
  box-shadow: inset 3px 0 0 #9CBE7E;
}

/* Footer leftAction — long trash-after label was crowding Cancel. */
.email-bulk-trash-after-connect {
  max-width: 58%;
  min-width: 0;
}
.email-bulk-trash-after-connect >>> .v-label {
  font-size: 0.75rem;
  line-height: 1.25;
}
</style>

<!-- Unscoped like PeopleSearch — target host (always in this SFC), not classes that may not land on VSimpleTable. -->
<style>
.email-table-host .v-data-table__wrapper {
  height: calc(100vh - var(--offset, 48px) - 140px) !important;
  max-height: calc(100vh - var(--offset, 48px) - 140px) !important;
  overflow-x: auto !important; /* rows h-scroll when narrow (table min-width) */
  overflow-y: auto !important;
}
.email-page--batch-banner .email-table-host .v-data-table__wrapper {
  height: calc(100vh - var(--offset, 48px) - 192px) !important;
  max-height: calc(100vh - var(--offset, 48px) - 192px) !important;
}
</style>
